import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useInView } from "framer-motion";
import { Target, HeartHandshake, CheckCircle2, Quote, Youtube, Instagram, MessageCircle, ArrowRight } from "lucide-react";
import { STATS, INSTRUCTORS, WHY, REVIEWS, PACKAGES, SOCIAL, IMAGES } from "@/data/content";
import { Section, Title, Reveal, Stars, PackageCard } from "@/components/site/ui";

const Counter = ({ value, suffix, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let f = 0;
    const id = setInterval(() => { f += 1; setN((value * f) / 40); if (f >= 40) clearInterval(id); }, 30);
    return () => clearInterval(id);
  }, [inView, value]);
  return <span ref={ref}>{decimals ? n.toFixed(decimals) : Math.round(n).toLocaleString("en-IN")}{suffix}</span>;
};

export const StatsMission = () => (
  <Section id="stats">
    <div className="gold-frame"><div className="rounded-[1.8rem] bg-[var(--bg)] p-8 md:p-14 grid lg:grid-cols-2 gap-12 relative overflow-hidden">
      <div className="grid grid-cols-2 gap-6">
        {STATS.map((s, i) => (
          <div key={s.label} data-testid={`stat-${i}`} className="rounded-3xl p-6 border border-[var(--surface-border)] text-center">
            <p className="font-display text-5xl font-bold neon-text"><Counter {...s} /></p>
            <p className="text-sm text-[var(--muted)] mt-2">{s.label}</p>
          </div>
        ))}
      </div>
      <div>
        <span className="eyebrow">Our Mission</span>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mt-5 leading-tight">Every woman deserves a <span className="neon-text">skill of her own</span></h2>
        {[[Target, "Make digital careers reachable", "Affordable, Hinglish-friendly courses so no girl is left behind because of fees or language."],
          [HeartHandshake, "Grow together as a sisterhood", "A supportive community where learners help each other find clients, jobs and confidence."]].map(([I, t, d]) => (
          <div key={t} className="flex gap-4 mt-7">
            <div className="icon-orb shrink-0"><I size={20} /></div>
            <div><h3 className="font-display text-lg font-semibold">{t}</h3><p className="text-sm text-[var(--muted)] mt-1">{d}</p></div>
          </div>
        ))}
      </div>
    </div></div>
  </Section>
);

export const InstructorSlider = () => (
  <Section id="instructors">
    <Title eyebrow="Instructors" title="Learn from women who" highlight="have done it" center />
    <div className="overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]">
      <div className="marquee marquee-slow gap-6">
        {[...INSTRUCTORS, ...INSTRUCTORS].map((t, i) => (
          <div key={i} data-testid={i < INSTRUCTORS.length ? `instructor-slide-${i}` : undefined} className="glass neon-card rounded-3xl p-4 w-64 mx-3 shrink-0">
            <img src={t.image} alt={t.name} className="w-full h-64 object-cover rounded-2xl" />
            <h3 className="font-display text-lg font-semibold mt-4">{t.name}</h3>
            <p className="text-xs text-[var(--pink)] font-semibold">{t.role} · {t.exp}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="text-center mt-10"><Link to="/instructors" data-testid="all-instructors-btn" className="btn-ghost">Meet All Mentors <ArrowRight size={16} /></Link></div>
  </Section>
);

export const WhyChoose = () => (
  <Section id="why">
    <div className="grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <img src={IMAGES.why} alt="Learner studying at home" className="rounded-[2.5rem] w-full h-[480px] object-cover border-2 border-[var(--gold)] shadow-[0_0_50px_-12px_var(--glow-gold)]" />
        <div className="glass absolute -bottom-6 right-6 rounded-2xl px-5 py-4 pulse-glow">
          <p className="font-display text-3xl font-bold neon-text">92%</p><p className="text-xs text-[var(--muted)]">finish their course</p>
        </div>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Title eyebrow="Why Choose Ilmora" title="Designed around" highlight="your life" center={false} />
        <div className="space-y-5">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div data-testid={`why-item-${i}`} className="flex gap-4">
                <CheckCircle2 className="text-[var(--pink)] shrink-0 mt-1 drop-shadow-[0_0_8px_var(--glow-pink)]" size={22} />
                <div><h3 className="font-display text-lg font-semibold">{w.title}</h3><p className="text-sm text-[var(--muted)] mt-1">{w.text}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

export const Stories = () => (
  <Section id="stories">
    <Title eyebrow="Success Stories" title="Real learners," highlight="real change" center />
    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [&>*]:mb-6">
      {REVIEWS.map((r, i) => (
        <Reveal key={r.name} delay={(i % 3) * 0.1} className="break-inside-avoid">
          <div data-testid={`review-card-${i}`} className="glass neon-card rounded-3xl p-7">
            <Quote className="text-[var(--pink)] opacity-60" size={28} />
            <p className="mt-3 text-sm leading-relaxed">{r.text}</p>
            <div className="flex items-center justify-between mt-5">
              <div><p className="font-bold">{r.name}</p><p className="text-xs text-[var(--muted)]">{r.city} · {r.course}</p></div>
              <Stars n={r.rating} />
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const COMMUNITY = [
  { k: "youtube", Icon: Youtube, title: "YouTube", text: "Free tutorials, live Q&As and student showcases every week.", cta: "Subscribe", href: SOCIAL.youtube },
  { k: "instagram", Icon: Instagram, title: "Instagram", text: "Daily tips, reels and behind-the-scenes from our mentors.", cta: "Follow", href: SOCIAL.instagram },
  { k: "whatsapp", Icon: MessageCircle, title: "WhatsApp Community", text: "Course updates, job leads and sisterly support in one group.", cta: "Join Group", href: SOCIAL.whatsapp },
];

export const Community = () => (
  <Section id="community">
    <Title eyebrow="Community" title="You're never" highlight="learning alone" center />
    <div className="grid md:grid-cols-3 gap-6">
      {COMMUNITY.map((c, i) => (
        <Reveal key={c.k} delay={i * 0.1}>
          <a href={c.href} target="_blank" rel="noreferrer" data-testid={`community-${c.k}`} className="glass neon-card rounded-3xl p-8 block h-full group">
            <div className="icon-orb !w-14 !h-14 group-hover:scale-110 transition-transform"><c.Icon size={26} /></div>
            <h3 className="font-display text-xl font-semibold mt-5">{c.title}</h3>
            <p className="text-sm text-[var(--muted)] mt-2">{c.text}</p>
            <span className="inline-flex items-center gap-2 mt-5 text-sm font-bold text-[var(--pink)]">{c.cta} <ArrowRight size={15} /></span>
          </a>
        </Reveal>
      ))}
    </div>
  </Section>
);

export const Packages = () => (
  <Section id="packages">
    <div className="flex flex-wrap items-end justify-between gap-6">
      <Title eyebrow="Learning Packages" title="Choose your" highlight="growth plan" center={false} />
      <Link to="/packages" data-testid="view-all-packages-btn" className="btn-ghost mb-8">All Packages <ArrowRight size={16} /></Link>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {PACKAGES.map((p, i) => <PackageCard key={p.slug} p={p} i={i} />)}
    </div>
  </Section>
);
